"use client";

import { useEffect, useMemo, useState } from "react";
import type { CRegisterationToNewslettersContent } from "@/lib/api/cms-catalog";
import type { FrontendUserSession } from "@/lib/api/session";
import {
  Field,
  Input,
  Checkbox,
  FormStatus,
  Honeypot,
  SubmitButton,
} from "@/components/ui/Form";
import { toast } from "@/lib/notifications/toast";
import { useT } from "@/lib/i18n/context";
import { useAntiSpamToken } from "@/lib/cms/useAntiSpamToken";
import {
  cRegisterationToNewslettersTranslations,
  type CRegisterationToNewslettersTranslations,
} from "./CRegisterationToNewsletters.i18n";
import styles from "./CRegisterationToNewsletters.module.css";

/**
 * CRegisterationToNewsletters - visitor view komponentu
 * `c_registeration_to_newsletters` (Zapis do newslettera).
 *
 * **Data source:** `C_REGISTERATION_TO_NEWSLETTERS_CONTROLLER::getApiContent()`
 * zwraca listę publicznych kategorii, pola dodatkowe (config 0/1/2), flagi+teksty
 * zgód oraz anti-spam token.
 *
 * **Dwa tryby (rozróżniane przez `userSession`):**
 *   - **Gość:** kategorie + pola dodatkowe + email + zgody + honeypot.
 *     Submit -> quickRegister konta subskrybenta + zapis (backend).
 *   - **Zalogowany:** TYLKO kategorie (pre-zaznaczone aktualnymi subskrypcjami
 *     pobranymi z `/subscriptions`). Submit -> aktualizacja subskrypcji. BEZ
 *     email/zgód/honeypotu (parytet z legacy `defaultAction`).
 *
 * **Anti-spam (gość, 4 warstwy bez reCAPTCHA - jak c_contact_form):** HMAC token,
 * min-delay (mierzony lokalnie od mountu), honeypot, rate-limit (backend).
 *
 * Tytuł strony (h1) renderuje kontener `CDefault` - ten komponent renderuje
 * tylko zawartość formularza.
 */
export function CRegisterationToNewsletters({
  content,
  userSession,
}: CRegisterationToNewslettersProps) {
  const t = useT(cRegisterationToNewslettersTranslations);

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <FormStatus variant="error">{t.noConfig}</FormStatus>
      </div>
    );
  }

  if (content.config_error) {
    return (
      <div className={styles.root}>
        <FormStatus variant="warning">{content.config_error}</FormStatus>
      </div>
    );
  }

  if (!Array.isArray(content.categorys) || content.categorys.length === 0) {
    return (
      <div className={styles.root}>
        <FormStatus variant="info">
          {content.lang.no_categorys || t.noCategorys}
        </FormStatus>
      </div>
    );
  }

  return (
    <NewsletterForm
      content={content}
      isLogged={!!userSession}
      userEmail={userSession?.user?.email ?? null}
      t={t}
    />
  );
}

export type CRegisterationToNewslettersProps = {
  content: CRegisterationToNewslettersContent;
  /** SSR-resolved sesja frontendowa - decyduje o trybie (gość vs zalogowany). */
  userSession?: FrontendUserSession | null;
};

/* ─── Form view ────────────────────────────────────────────────────────── */

function NewsletterForm({
  content,
  isLogged,
  userEmail,
  t,
}: {
  content: CRegisterationToNewslettersContent;
  isLogged: boolean;
  /** E-mail zalogowanego usera (z sesji) - subskrypcje dotyczą JEGO konta,
      nie adresu wpisanego w oknie teasera w_newsletter. */
  userEmail?: string | null;
  t: CRegisterationToNewslettersTranslations;
}) {
  const fields = useMemo(
    () => (Array.isArray(content.fields) ? content.fields : []),
    [content.fields],
  );

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(() => {
    const dv: Record<string, string> = {};
    for (const f of fields) dv[f.name] = "";
    return dv;
  });
  const [email, setEmail] = useState("");
  const [regulamin, setRegulamin] = useState(false);
  const [personalData, setPersonalData] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [honeypot, setHoneypot] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [topError, setTopError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /* Lokalny czas mountu - min-delay check niezależnie od server timestamp. */
  const [mountedAt] = useState(() => Date.now());

  /* Fresh anti-spam token per mount (tylko gosc - zalogowany user nie ma
     antispam payloadu). Pusty scope dla zalogowanego -> hook `ready=true`
     bez fetch'a. */
  const antispam = useAntiSpamToken(isLogged ? [] : [content.id]);

  /* Prefill e-mail z `?email=` (okno w_newsletter przekierowuje tutaj z adresem
     wpisanym w teaserze). Czytamy z URL po stronie klienta (bez useSearchParams
     - unikamy wymogu Suspense). Tylko gość ma pole email do uzupełnienia. */
  useEffect(() => {
    if (isLogged) return;
    try {
      const param = new URLSearchParams(window.location.search).get("email");
      /* Prefill z URL po odczycie systemu zewnętrznego (location) - celowe,
         client-only (analog mount-guarda / WWindowBanner). */
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if (param) setEmail((prev) => (prev ? prev : param.trim()));
    } catch {
      /* brak window / niepoprawny URL - ignoruj */
    }
  }, [isLogged]);

  /* Zalogowany: pobierz aktualne subskrypcje i pre-zaznacz kategorie. */
  useEffect(() => {
    if (!isLogged) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/c-registeration-to-newsletters/subscriptions?id_catalog=${content.id}`,
          { cache: "no-store" },
        );
        if (!res.ok) return;
        const json = (await res.json().catch(() => ({}))) as {
          subscribed?: number[];
        };
        if (!cancelled && Array.isArray(json.subscribed)) {
          setSelected(new Set(json.subscribed.map((n) => Number(n))));
        }
      } catch {
        /* fail-soft - brak pre-zaznaczenia, user zaznacza ręcznie. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLogged, content.id]);

  const toggleCategory = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const labels = content.lang.labels ?? {};
  const consentTexts = content.lang.consent_texts ?? {};

  const validate = (): Record<string, string> => {
    const errs: Record<string, string> = {};

    if (selected.size === 0)
      errs.categorys = content.lang.select_categorys || t.validation.selectCategory;

    /* Pola/email/zgody walidowane TYLKO dla gościa. */
    if (!isLogged) {
      for (const f of fields) {
        if (f.required && (fieldValues[f.name] ?? "").trim() === "")
          errs[f.name] = t.validation.requiredField(f.label);
      }

      const em = email.trim();
      if (em === "")
        errs.email = t.validation.requiredField(labels.email || t.emailLabel);
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em))
        errs.email = t.validation.invalidEmail;

      if (content.consents.regulamin && !regulamin)
        errs.regulamin = t.validation.notAcceptedRegulamin;
      if (content.consents.personal_data && !personalData)
        errs.personal_data = t.validation.notAcceptedPersonalData;
      if (content.consents.marketing && !marketing)
        errs.marketing = t.validation.notAcceptedMarketing;
    }

    return errs;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTopError(null);

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});

    /* Min-delay (anti-spam) - tylko gość ma anti-spam payload. */
    if (!isLogged && content.antispam) {
      const ageSec = Math.floor((Date.now() - mountedAt) / 1000);
      if (ageSec < content.antispam.min_delay_seconds) {
        setTopError(
          t.status.waitBeforeSubmit(content.antispam.min_delay_seconds - ageSec),
        );
        return;
      }
      if (!antispam.token || !antispam.timestamp) {
        setTopError(antispam.error ?? t.status.subscribeFailed);
        return;
      }
    }

    const body: Record<string, unknown> = {
      id_catalog: content.id,
      categorys: Array.from(selected),
    };

    if (!isLogged && content.antispam && antispam.token && antispam.timestamp) {
      const fieldPayload: Record<string, string> = {};
      for (const f of fields) fieldPayload[f.name] = (fieldValues[f.name] ?? "").trim();

      body.token = antispam.token;
      body.timestamp = antispam.timestamp;
      body[content.antispam.honeypot_field] = honeypot;
      body.email = email.trim();
      body.field = fieldPayload;
      body.regulamin = regulamin;
      body.personal_data = personalData;
      body.marketing = marketing;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`/api/c-registeration-to-newsletters/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        message?: string;
        code?: number;
        errors?: Record<string, string>;
      };

      if (!res.ok || !json.ok) {
        /* Server-side per-pole errors -> pod konkretne inputy. */
        if (
          json.errors &&
          typeof json.errors === "object" &&
          Object.keys(json.errors).length > 0
        ) {
          setErrors(json.errors);
        } else {
          setTopError(
            json.message ||
              (res.status === 429 ? t.status.rateLimit : t.status.subscribeFailed),
          );
        }
        return;
      }

      const successMsg = isLogged
        ? content.lang.messages?.update_ok || t.status.updateSuccess
        : content.lang.messages?.save_ok || t.status.subscribeSuccess;
      toast.success(json.message || successMsg);

      /* Gość: reset formularza po zapisie (nowy subskrybent). Zalogowany:
         zostawiamy zaznaczenia (to jego aktualny stan subskrypcji). */
      if (!isLogged) {
        setEmail("");
        setRegulamin(false);
        setPersonalData(false);
        setMarketing(false);
        setHoneypot("");
        setFieldValues(() => {
          const dv: Record<string, string> = {};
          for (const f of fields) dv[f.name] = "";
          return dv;
        });
        setSelected(new Set());
      }
    } catch (err) {
      setTopError(
        err instanceof Error
          ? t.status.networkError(err.message)
          : t.status.networkErrorGeneric,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.root}>
      {isLogged ? (
        <p className={styles.intro}>{t.loggedInIntro(userEmail ?? "")}</p>
      ) : (
        content.lang.top_info && (
          <p className={styles.intro}>{content.lang.top_info}</p>
        )
      )}

      <form className={styles.form} onSubmit={onSubmit} noValidate>
        {/* ─── Kategorie ─── */}
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>
            {!isLogged && content.lang.top_info_2
              ? content.lang.top_info_2
              : t.categoriesHeader}
          </legend>
          <div className={styles.categories} role="group">
            {content.categorys.map((cat) => (
              <Checkbox
                key={cat.id}
                checked={selected.has(cat.id)}
                onChange={() => toggleCategory(cat.id)}
                disabled={submitting}
              >
                {cat.name}
              </Checkbox>
            ))}
          </div>
          {errors.categorys && (
            <div className={styles.fieldError} role="alert">
              {errors.categorys}
            </div>
          )}
        </fieldset>

        {/* ─── Pola dodatkowe + email + zgody (tylko gość) ─── */}
        {!isLogged && (
          <>
            <fieldset className={styles.fieldset}>
              <legend className={styles.legend}>{t.additionalDataHeader}</legend>
              <div className={styles.fieldsGrid}>
                {fields.map((f) => (
                  <Field
                    key={f.name}
                    label={f.label}
                    required={f.required}
                    error={errors[f.name]}
                  >
                    <Input
                      type="text"
                      value={fieldValues[f.name] ?? ""}
                      onChange={(ev) =>
                        setFieldValues((prev) => ({
                          ...prev,
                          [f.name]: ev.target.value,
                        }))
                      }
                      disabled={submitting}
                    />
                  </Field>
                ))}

                <Field
                  label={labels.email || t.emailLabel}
                  required
                  error={errors.email}
                >
                  <Input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                    disabled={submitting}
                  />
                </Field>
              </div>
            </fieldset>

            {(content.consents.regulamin ||
              content.consents.personal_data ||
              content.consents.marketing) && (
              <div className={styles.consents}>
                {content.consents.regulamin && (
                  <Checkbox
                    checked={regulamin}
                    onChange={(ev) => setRegulamin(ev.target.checked)}
                    disabled={submitting}
                    error={errors.regulamin}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: consentTexts.regulamin ?? "",
                      }}
                    />
                  </Checkbox>
                )}
                {content.consents.personal_data && (
                  <Checkbox
                    checked={personalData}
                    onChange={(ev) => setPersonalData(ev.target.checked)}
                    disabled={submitting}
                    error={errors.personal_data}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: consentTexts.personal_data ?? "",
                      }}
                    />
                  </Checkbox>
                )}
                {content.consents.marketing && (
                  <Checkbox
                    checked={marketing}
                    onChange={(ev) => setMarketing(ev.target.checked)}
                    disabled={submitting}
                    error={errors.marketing}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: consentTexts.marketing ?? "",
                      }}
                    />
                  </Checkbox>
                )}
              </div>
            )}

            {/* Honeypot poza widocznym formularzem - boty fillują, ludzie nie. */}
            {content.antispam && (
              <Honeypot
                name={content.antispam.honeypot_field}
                value={honeypot}
                onChange={(ev) => setHoneypot(ev.target.value)}
              />
            )}
          </>
        )}

        {topError && <FormStatus variant="error">{topError}</FormStatus>}

        <div className={styles.actions}>
          <SubmitButton
            loading={submitting || !antispam.ready}
            loadingLabel={t.saving}
            disabled={antispam.error != null}
          >
            {isLogged
              ? t.saveLoggedIn
              : content.lang.save || t.save}
          </SubmitButton>
        </div>
      </form>
    </div>
  );
}
